import React, { useState, ChangeEventHandler, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, Button, Collapse, CardBody, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import StatusCode from '../../../../../../util/StatusCode';
import EnumInput from '../../../../../misc/EnumInput';
import Loading from '../../../../../misc/Loading';
import ModalComponent from '../../../../../misc/ModalComponent';
import AsmMapping from '../AsmMapping';
import InformationMarkingsInput from '../../components/InformationMarkingsInput';
import { CoreAppleSchoolManagerConfiguration, AppleSchoolManagerUserMappingType, gradeLevelMappingOptions, classSourceSelectionOptions, AppleSchoolManagerGradeLevelMappingType } from '../../../../../../models/CoreAsmModels';
import InstanceClient from '../../../../../../services/InstanceClient';



export const validator = (configuration: CoreAppleSchoolManagerConfiguration, active: boolean) => {
    const validation = [];

    if (active) {
        if (!configuration.sftpHostname || configuration.sftpHostname === '') {
            validation.push('sftpHostname');
        }

        if (!configuration.sftpUsername || configuration.sftpUsername === '') {
            validation.push('sftpUsername');
        }

        if (!configuration.sftpPassword || configuration.sftpPassword === '') {
            validation.push('sftpPassword');
        }
    }

    if (configuration.personIdMappingType === AppleSchoolManagerUserMappingType.ONPREMISESEXTENSIONATTRIBUTE && !configuration.personIdMappingValue) {
        validation.push('personIdMappingValue');
    }

    if (configuration.personNumberMappingType === AppleSchoolManagerUserMappingType.ONPREMISESEXTENSIONATTRIBUTE && !configuration.personNumberMappingValue) {
        validation.push('personNumberMappingValue');
    }

    if (configuration.sisUserNameMappingType === AppleSchoolManagerUserMappingType.ONPREMISESEXTENSIONATTRIBUTE && !configuration.sisUserNameMappingValue) {
        validation.push('sisUserNameMappingValue');
    }

    if (configuration.emailAddressMappingType === AppleSchoolManagerUserMappingType.ONPREMISESEXTENSIONATTRIBUTE && !configuration.emailAddressMappingValue) {
        validation.push('emailAddressMappingValue');
    }

    if (configuration.gradeLevelMappingType !== AppleSchoolManagerGradeLevelMappingType.STANDARD
        &&
        configuration.gradeLevelMappingType !== AppleSchoolManagerGradeLevelMappingType.UNIQUE) {
        validation.push('gradeLevelMappingType');
    }

    return validation;
}

interface AppleSchoolManagerExportConfigurationProps {
    disabled: boolean
    configuration: CoreAppleSchoolManagerConfiguration
    updatePart: (part: Partial<CoreAppleSchoolManagerConfiguration>) => void
    active: boolean
    onValidChange: (arg: boolean) => void 
}

interface FingerPrintModal {
    isOpen: boolean,
    reason: string | null,
    operationStatus: {
        code: StatusCode,
        error?: string
    }
}


const AppleSchoolManagerExportConfiguration = (props: AppleSchoolManagerExportConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);

    const [open, setOpen] = useState<boolean>(true);
    const [resetFingerprintModal, setResetFingerprintModal] = useState<FingerPrintModal>({
        isOpen: false,
        reason: null,
        operationStatus: {
            code: StatusCode.NONE,
            error: undefined,
        }
    })

    const validation = validator(props.configuration, props.active);

    useEffect(() => {
        props.onValidChange(validation.length ? true : false)
    }, [props.configuration, props.active])

    const setResetFingerprintModalState = (state: Partial<FingerPrintModal>) => {
        setResetFingerprintModal({
                ...resetFingerprintModal,
                ...state,
        })
    }

    const updateResetFingerprintModalReason: ChangeEventHandler<HTMLInputElement> = (e) => {
        setResetFingerprintModalState({ reason: e.target.value });
    };

    const closeResetFingerprintModal = (id: string, confirmed: boolean) => {
        if (confirmed) {
            setResetFingerprintModalState({
                operationStatus: {
                    code: StatusCode.PENDING
                }
            });

            const body = {
                reason: resetFingerprintModal.reason
            };

            InstanceClient.post(`/api/core/appleschoolmanagerconfiguration/${id}/clearsftpfingerprint`, body)
                .then(_ => setResetFingerprintModalState({
                    isOpen: false,
                    reason: null,
                    operationStatus: {
                        code: StatusCode.COMPLETE
                    }
                }))
                .catch(error => setResetFingerprintModalState({
                    operationStatus: {
                        code: StatusCode.ERROR,
                        error,
                    }
                }));
        } else {
            setResetFingerprintModalState({
                isOpen: false,
                reason: null,
                operationStatus: {
                    code: StatusCode.COMPLETE
                }
            })
        }
    }

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open) }>{open ? '-' : '+'}</Button>
                {t('modules.AppleSchoolManager.title', { ns: 'common' })}
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>
                    <div style={{ margin: '1rem 0' }}>
                        <FormGroup>
                            <Label for="sftpHostname">{t('asm.sftpHostname', { ns: 'configuration' })}</Label>
                            <Input
                                type="text"
                                name="sftpHostname"
                                id="sftpHostname"
                                value={props.configuration.sftpHostname || ''}
                                disabled={props.disabled}
                                onChange={(e) => props.updatePart({ sftpHostname: e.target.value })}
                                invalid={validation.indexOf('sftpHostname') !== -1}
                                autoComplete="off"
                            />
                            <FormFeedback>{t('asm.sftpHostnameFeedback', { ns: 'configuration' })}</FormFeedback>
                        </FormGroup>
                        <FormGroup>
                            <Label for="sftpUsername">{t('asm.sftpUsername', { ns: 'configuration' })}</Label>
                            <Input
                                type="text"
                                name="sftpUsername"
                                id="sftpUsername"
                                value={props.configuration.sftpUsername || ''}
                                disabled={props.disabled}
                                onChange={(e) => props.updatePart({ sftpUsername: e.target.value })}
                                invalid={validation.indexOf('sftpUsername') !== -1}
                                autoComplete="off"
                            />
                            <FormFeedback>{t('asm.sftpUsernameFeedback', { ns: 'configuration' })}</FormFeedback>
                        </FormGroup>
                        <FormGroup>
                            <Label for="sftpPassword">{t('asm.sftpPassword', { ns: 'configuration' })}</Label>
                            <Input
                                type="text"
                                name="sftpPassword"
                                id="sftpPassword"
                                value={props.configuration.sftpPassword || ''}
                                disabled={props.disabled}
                                onChange={(e) => props.updatePart({ sftpPassword: e.target.value })}
                                invalid={validation.indexOf('sftpPassword') !== -1}
                                autoComplete="off"
                            />
                            <FormFeedback>{t('asm.sftpPasswordFeedback', { ns: 'configuration' })}</FormFeedback>
                        </FormGroup>
                        <FormGroup>
                            <div className="edit_module">
                                <Button
                                    className="flowsync-btn"
                                    disabled={props.disabled}
                                    onClick={(e) => setResetFingerprintModalState({ isOpen: true })}
                                >{t('asm.resetFingerPrint.btn', { ns: 'configuration' })}</Button>
                            </div>
                        </FormGroup>
                        <ModalComponent
                            isOpen={resetFingerprintModal.isOpen}
                            toggleModal={() => closeResetFingerprintModal(props.configuration.id, false)}
                            header={t('asm.resetFingerPrint.title', { ns: 'configuration' })}
                            size="xl"
                            disabled={resetFingerprintModal.operationStatus.code === StatusCode.PENDING}
                            update={() => closeResetFingerprintModal(props.configuration.id, true)}
                            confirm={t('asm.resetFingerPrint.confirm', { ns: 'configuration' })}
                            cancel={t('action.cancel', { ns: 'common' })}
                        >
                            <p>{t('asm.resetFingerPrint.bodyText', { ns: 'configuration' })}</p>
                            <FormGroup>
                                <Label for="resetFingerprintReason">{t('asm.resetFingerPrint.reason', { ns: 'configuration' })}</Label>
                                <Input
                                    type="text"
                                    id="resetFingerprintReason"
                                    disabled={resetFingerprintModal.operationStatus.code === StatusCode.PENDING}
                                    value={resetFingerprintModal.reason ?? ''}
                                    onChange={updateResetFingerprintModalReason} />
                                {resetFingerprintModal.operationStatus.code === StatusCode.PENDING && <Loading />}
                                {resetFingerprintModal.operationStatus.code === StatusCode.ERROR && <p style={{ color: 'red' }}>{t('asm.resetFingerPrint.error', { ns: 'configuration' })}</p>}
                            </FormGroup>
                        </ModalComponent>

                        <FormGroup>
                            <EnumInput
                                title={t('asm.createClassesBasedOn', { ns: 'configuration' })}
                                multi={true}
                                name="classSourceSelection"
                                value={props.configuration.classSourceSelection}
                                options={classSourceSelectionOptions.map(x => ({ value: x, text: t(`asm.classSourceTypes.${x}`, { ns: 'configuration' }) }))}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled}
                            />
                        </FormGroup>

                        <h5>{t('asm.userColumnMapping.title', { ns: 'configuration' })}</h5>
                        <AsmMapping
                            title={t('asm.userColumnMapping.personId.title', { ns: 'configuration' })}
                            defaultText={t('asm.userColumnMapping.personId.default', { ns: 'configuration' })}
                            disabled={props.disabled}
                            value={{ mappingType: props.configuration.personIdMappingType, mappingValue: props.configuration.personIdMappingValue }}
                            name={{ mappingType: "personIdMappingType", mappingValue: "personIdMappingValue" }}
                            onChange={(p: any) => props.updatePart(p)}
                            invalidFeedback={t('asm.userColumnMapping.invalidFeedback', { ns: 'configuration' })}
                            invalid={validation.indexOf('personIdMappingValue') !== -1}
                        />

                        <AsmMapping
                            title={t('asm.userColumnMapping.personNumber.title', { ns: 'configuration' })}
                            defaultText={t('asm.userColumnMapping.personNumber.default', { ns: 'configuration' })}
                            disabled={props.disabled}
                            value={{ mappingType: props.configuration.personNumberMappingType, mappingValue: props.configuration.personNumberMappingValue }}
                            name={{ mappingType: "personNumberMappingType", mappingValue: "personNumberMappingValue" }}
                            onChange={(p: any) => props.updatePart(p)}
                            invalidFeedback={t('asm.userColumnMapping.invalidFeedback', { ns: 'configuration' })}
                            invalid={validation.indexOf('personNumberMappingValue') !== -1}
                        />

                        <AsmMapping
                            title={t('asm.userColumnMapping.sisUsername.title', { ns: 'configuration' })}
                            defaultText={t('asm.userColumnMapping.sisUsername.default', { ns: 'configuration' })}
                            disabled={props.disabled}
                            value={{ mappingType: props.configuration.sisUserNameMappingType, mappingValue: props.configuration.sisUserNameMappingValue }}
                            name={{ mappingType: "sisUserNameMappingType", mappingValue: "sisUserNameMappingValue" }}
                            onChange={(p: any) => props.updatePart(p)}
                            invalidFeedback={t('asm.userColumnMapping.invalidFeedback', { ns: 'configuration' })}
                            invalid={validation.indexOf('sisUserNameMappingValue') !== -1}
                        />

                        <AsmMapping
                            title={t('asm.userColumnMapping.emailAddress.title', { ns: 'configuration' })}
                            defaultText={t('asm.userColumnMapping.emailAddress.default', { ns: 'configuration' })}
                            disabled={props.disabled}
                            value={{ mappingType: props.configuration.emailAddressMappingType, mappingValue: props.configuration.emailAddressMappingValue }}
                            name={{ mappingType: "emailAddressMappingType", mappingValue: "emailAddressMappingValue" }}
                            onChange={(p: any) => props.updatePart(p)}
                            invalidFeedback={t('asm.userColumnMapping.invalidFeedback', { ns: 'configuration' })}
                            invalid={validation.indexOf('emailAddressMappingValue') !== -1}
                        />

                        <h5>{t('asm.gradeLevelMapping.title', { ns: 'configuration' })}</h5>
                        <EnumInput
                            title={t('asm.gradeLevelMapping.enumTitle', { ns: 'configuration' })}
                            titleSmall={true}
                            name='gradeLevelMappingType'
                            value={props.configuration.gradeLevelMappingType}
                            options={gradeLevelMappingOptions.map(x => ({ value: x, text: t(`asm.gradeLevelMappingTypes.${x}`, { ns: 'configuration' }) }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                            invalid={validation.indexOf('gradeLevelMappingType') !== -1}
                            invalidFeedback={t('asm.gradeLevelMapping.invalidFeedback', { ns: 'configuration' })}
                        />

                        <InformationMarkingsInput
                            selectedValues={props.configuration.activityInformationMarkingFilters}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            name='activityInformationMarkingFilters'
                        />

                    </div>
                </CardBody>
            </Collapse>
        </Card>
    );
}

export default AppleSchoolManagerExportConfiguration;
